
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
    ArrowRight,
    ArrowUpRight,
    Bookmark,
    Check,
    ChevronRight,
    Compass,
    Heart,
    Hotel,
    ImageOff,
    MapPin,
    Search,
    ShieldCheck,
    Sparkles,
    Trash2,
} from "lucide-react";

import heroImg from "../assests/images/hero.jpg";

/* ============================================================
   CONFIGURATION
============================================================ */

const HOTEL_KEY = "dubai_hotel_wishlist";
const AREA_KEY = "dubai_saved_areas";
const LEGACY_KEY = "wishlist";

const HOTEL_IMAGE_BASE =
    "https://uaediscountedhotels.com/uploads/hotels";

/* ============================================================
   STORAGE HELPERS
============================================================ */

function readItems(key) {
    try {
        const items = JSON.parse(localStorage.getItem(key) || "[]");

        return Array.isArray(items) ? items : [];
    } catch {
        return [];
    }
}

function itemId(item) {
    return String(
        item?.id ?? item?.slug ?? item?.name ?? ""
    );
}

function normalizeHotel(hotel) {
    return {
        ...hotel,
        id: itemId(hotel),
        name: hotel.name || hotel.hotel_name || "Hotel",
        image:
            hotel.image ||
            hotel.featured_image ||
            hotel.thumbnail ||
            "",
    };
}

function normalizeArea(area) {
    return {
        ...area,
        id: itemId(area),
        name: area.name || area.area_name || "Dubai Area",
        image:
            area.image ||
            area.featured_image ||
            area.thumbnail ||
            "",
    };
}

function imageSrc(value) {
    if (!value) return "";

    const image =
        typeof value === "object"
            ? value.url ||
            value.image_url ||
            value.path ||
            value.image
            : value;

    if (typeof image !== "string" || !image.trim()) {
        return "";
    }

    if (/^https?:\/\//i.test(image)) {
        return image;
    }

    if (image.startsWith("//")) {
        return `https:${image}`;
    }

    if (image.startsWith("/")) {
        return image;
    }

    return `${HOTEL_IMAGE_BASE}/${image.replace(
        /^uploads\/hotels\//,
        ""
    )}`;
}

/* ============================================================
   IMAGE COMPONENT
============================================================ */

function SavedImage({ image, name, type }) {
    const [failed, setFailed] = useState(false);

    const src = imageSrc(image);

    useEffect(() => {
        setFailed(false);
    }, [src]);

    return (
        <div className="relative h-[230px] overflow-hidden bg-[#064E3B] sm:h-[240px]">
            {src && !failed ? (
                <img
                    src={src}
                    alt={name}
                    loading="lazy"
                    onError={() => setFailed(true)}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
            ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-[#064E3B] to-[#17324D] text-white/60">
                    {type === "areas" ? (
                        <MapPin size={36} strokeWidth={1.4} />
                    ) : (
                        <ImageOff size={36} strokeWidth={1.4} />
                    )}

                    <span className="text-xs font-semibold">
                        {type === "areas"
                            ? "Dubai Destination"
                            : "Hotel Image"}
                    </span>
                </div>
            )}

            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#032F28]/50 via-transparent to-transparent" />
        </div>
    );
}

/* ============================================================
   SAVED ITEM CARD
============================================================ */

function SavedCard({
    item,
    type,
    onRemove,
    onOpen,
}) {
    const isArea = type === "areas";

    const name =
        item.name ||
        (isArea ? "Dubai Area" : "Dubai Hotel");

    return (
        <article className="group flex h-full flex-col overflow-hidden rounded-[22px] border border-[#E0ECE4] bg-white transition-all duration-300 hover:-translate-y-1 hover:border-[#B9DFC5] hover:shadow-[0_20px_45px_rgba(6,78,59,0.10)]">

            {/* IMAGE */}
            <div className="relative">
                <button
                    type="button"
                    onClick={onOpen}
                    aria-label={`Explore ${name}`}
                    className="block w-full text-left"
                >
                    <SavedImage
                        image={item.image}
                        name={name}
                        type={type}
                    />
                </button>

                {/* BADGE */}
                <div className="pointer-events-none absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-[10px] font-extrabold uppercase tracking-wider text-[#168B43] shadow-sm">
                    {isArea ? (
                        <MapPin size={13} />
                    ) : (
                        <Hotel size={13} />
                    )}

                    {isArea ? "Saved Destination" : "Saved Hotel"}
                </div>

                {/* REMOVE HEART */}
                <button
                    type="button"
                    onClick={onRemove}
                    title={`Remove ${name} from wishlist`}
                    aria-label={`Remove ${name} from wishlist`}
                    className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#E84C68] shadow-md transition hover:scale-110 hover:bg-[#FFF0F3] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#168B43]"
                >
                    <Heart
                        size={20}
                        className="fill-[#E84C68]"
                    />
                </button>
            </div>

            {/* CARD CONTENT */}
            <div className="flex flex-1 flex-col p-6">

                <div className="mb-3 flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#168B43]" />

                    <span className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#168B43]">
                        {isArea
                            ? "Explore Dubai"
                            : "Your Favorite Stay"}
                    </span>
                </div>

                <h3 className="line-clamp-2 min-h-[52px] text-[21px] font-extrabold leading-tight tracking-tight text-[#17324D] transition group-hover:text-[#168B43]">
                    {name}
                </h3>

                <p className="mt-3 flex items-start gap-2 text-[13px] leading-6 text-[#718096]">
                    <MapPin
                        size={16}
                        className="mt-1 shrink-0 text-[#168B43]"
                    />

                    <span className="line-clamp-2">
                        {isArea
                            ? "Dubai, United Arab Emirates"
                            : item.location || "Dubai, United Arab Emirates"}
                    </span>
                </p>

                {/* CARD FOOTER */}
                <div className="mt-auto flex items-center gap-3 border-t border-[#E8F0EB] pt-5">
                    <button
                        type="button"
                        onClick={onOpen}
                        className="group/btn inline-flex min-h-[47px] flex-1 items-center justify-center gap-2 rounded-xl bg-[#168B43] px-4 py-3 text-[13px] font-extrabold text-white transition hover:bg-[#087D37]"
                    >
                        {isArea ? "Explore Hotels" : "View Hotel"}

                        <ArrowRight
                            size={16}
                            className="transition group-hover/btn:translate-x-1"
                        />
                    </button>

                    <button
                        type="button"
                        onClick={onRemove}
                        aria-label={`Remove ${name}`}
                        title="Remove from wishlist"
                        className="flex h-[47px] w-[47px] shrink-0 items-center justify-center rounded-xl border border-[#E3ECE6] bg-[#F8FBF9] text-[#718096] transition hover:border-red-200 hover:bg-red-50 hover:text-red-500"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            </div>
        </article>
    );
}

/* ============================================================
   EMPTY WISHLIST STATE
============================================================ */

function EmptyWishlist({ type, onExplore }) {
    const isArea = type === "areas";

    return (
        <div className="relative overflow-hidden rounded-[24px] border border-[#DCEAE1] bg-white px-6 py-16 text-center sm:py-20">

            {/* DECORATIVE CIRCLES */}
            <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-[#E8F7EE]/70" />
            <div className="pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-[#E8F7EE]/70" />

            <div className="relative mx-auto max-w-[520px]">
                <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-[28px] bg-[#E8F7EE] text-[#168B43]">
                    {isArea ? (
                        <MapPin size={43} strokeWidth={1.6} />
                    ) : (
                        <Heart size={43} strokeWidth={1.6} />
                    )}
                </div>

                <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F0F8F3] px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.15em] text-[#168B43]">
                    <Sparkles size={14} />
                    Your Next Discovery Awaits
                </div>

                <h2 className="mt-5 text-[29px] font-extrabold tracking-tight text-[#17324D] sm:text-[36px]">
                    No Saved{" "}
                    <span className="text-[#168B43]">
                        {isArea ? "Areas" : "Hotels"} Yet.
                    </span>
                </h2>

                <p className="mx-auto mt-4 max-w-[430px] text-sm leading-8 text-[#718096]">
                    {isArea
                        ? "Explore Dubai's popular neighborhoods and save your favorite destinations for your next visit."
                        : "Discover amazing Dubai hotels and tap the heart icon to save your favorite stays in one convenient place."}
                </p>

                <button
                    type="button"
                    onClick={onExplore}
                    className="mt-8 inline-flex items-center justify-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white transition hover:-translate-y-0.5 hover:bg-[#087D37]"
                >
                    {isArea ? (
                        <Compass size={18} />
                    ) : (
                        <Hotel size={18} />
                    )}

                    {isArea
                        ? "Explore Popular Areas"
                        : "Discover Dubai Hotels"}

                    <ArrowRight size={17} />
                </button>
            </div>
        </div>
    );
}

/* ============================================================
   MAIN WISHLIST PAGE
============================================================ */

export default function Wishlist() {
    const navigate = useNavigate();

    const [tab, setTab] = useState("hotels");
    const [hotels, setHotels] = useState([]);
    const [areas, setAreas] = useState([]);
    const [notice, setNotice] = useState("");

    /* ==========================================================
       SYNCHRONIZE LOCAL STORAGE
    ========================================================== */

    const sync = useCallback(() => {
        const current = readItems(HOTEL_KEY).map(normalizeHotel);
        const legacy = readItems(LEGACY_KEY).map(normalizeHotel);

        const seen = new Set(
            current.map((hotel) => hotel.id)
        );

        const merged = [
            ...current,
            ...legacy.filter(
                (hotel) =>
                    hotel.id &&
                    !seen.has(hotel.id) &&
                    seen.add(hotel.id)
            ),
        ];

        setHotels(merged);
        setAreas(readItems(AREA_KEY).map(normalizeArea));
    }, []);

    useEffect(() => {
        sync();

        window.addEventListener("storage", sync);
        window.addEventListener("hotel-wishlist-updated", sync);
        window.addEventListener("saved-areas-changed", sync);

        return () => {
            window.removeEventListener("storage", sync);
            window.removeEventListener(
                "hotel-wishlist-updated",
                sync
            );
            window.removeEventListener(
                "saved-areas-changed",
                sync
            );
        };
    }, [sync]);

    /* ==========================================================
       WISHLIST DATA
    ========================================================== */

    const items = tab === "hotels" ? hotels : areas;

    const total = hotels.length + areas.length;

    const tabs = useMemo(
        () => [
            {
                id: "hotels",
                label: "Saved Hotels",
                count: hotels.length,
                icon: Hotel,
            },
            {
                id: "areas",
                label: "Saved Areas",
                count: areas.length,
                icon: MapPin,
            },
        ],
        [hotels.length, areas.length]
    );

    /* ==========================================================
       REMOVE SAVED ITEM
    ========================================================== */

    const remove = (item) => {
        const key = tab === "hotels" ? HOTEL_KEY : AREA_KEY;

        try {
            if (tab === "hotels") {
                localStorage.setItem(
                    HOTEL_KEY,
                    JSON.stringify(
                        readItems(HOTEL_KEY).filter(
                            (hotel) => itemId(hotel) !== item.id
                        )
                    )
                );

                localStorage.setItem(
                    LEGACY_KEY,
                    JSON.stringify(
                        readItems(LEGACY_KEY).filter(
                            (hotel) => itemId(hotel) !== item.id
                        )
                    )
                );

                window.dispatchEvent(
                    new Event("hotel-wishlist-updated")
                );
            } else {
                localStorage.setItem(
                    key,
                    JSON.stringify(
                        readItems(key).filter(
                            (area) => itemId(area) !== item.id
                        )
                    )
                );

                window.dispatchEvent(
                    new Event("saved-areas-changed")
                );
            }

            setNotice("");
            sync();
        } catch {
            setNotice(
                "Your browser couldn't update the wishlist. Please check browser storage settings."
            );
        }
    };

    /* ==========================================================
       OPEN SAVED ITEM
    ========================================================== */

    const open = (item) => {
        if (tab === "areas") {
            navigate("/search-hotels", {
                state: {
                    destination: "Dubai",
                    area: item.name,
                    location: item.name,
                    checkIn: "",
                    checkOut: "",
                    guests: 2,
                    rooms: 1,
                },
            });
        } else if (item.slug) {
            navigate(
                `/hotels/${encodeURIComponent(item.slug)}`
            );
        } else {
            navigate("/hotels");
        }

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    /* ==========================================================
       RENDER
    ========================================================== */

    return (
        <>
            <Helmet>
                <title>My Wishlist | Dubai Budgetive Hotels</title>
                <meta
                    name="description"
                    content="View your saved Dubai hotels and favorite areas in one place."
                />
                <meta name="robots" content="noindex, follow" />
            </Helmet>

            <main className="min-h-screen overflow-hidden bg-white text-[#17324D]">

                {/* =====================================================
            FULL WIDTH WISHLIST BANNER
        ===================================================== */}

                <section className="relative isolate flex min-h-[410px] items-center overflow-hidden bg-[#064E3B] pt-24 sm:min-h-[470px]">
                    <img
                        src={heroImg}
                        alt="Dubai skyline"
                        className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
                    />

                    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28]/95 via-[#064E3B]/85 to-[#064E3B]/55" />

                    <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#032F28]/40 to-transparent" />

                    <div className="mx-auto w-full max-w-[1536px] px-4 py-16 sm:px-6 lg:px-8">
                        <div className="max-w-[800px]">

                            {/* BREADCRUMB */}
                            <nav
                                aria-label="Breadcrumb"
                                className="mb-8 flex items-center gap-2 text-xs font-semibold text-white/75"
                            >
                                <Link
                                    to="/"
                                    className="transition hover:text-white"
                                >
                                    Home
                                </Link>

                                <ChevronRight size={14} />

                                <span className="text-[#B6F2CB]">
                                    My Wishlist
                                </span>
                            </nav>

                            {/* LABEL */}
                            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#B6F2CB] backdrop-blur-sm">
                                <Heart size={15} />
                                Your Saved Favorites
                            </div>

                            {/* TITLE */}
                            <h1 className="text-[43px] font-extrabold leading-[1.12] tracking-[-0.045em] text-white sm:text-[58px] lg:text-[68px]">
                                Your Favorite
                                <br />
                                <span className="text-[#A5E8B8]">
                                    Dubai Escapes.
                                </span>
                            </h1>

                            <p className="mt-6 max-w-[630px] text-[15px] leading-8 text-white/80 sm:text-base">
                                All the hotels and Dubai destinations you love,
                                saved in one place. Keep exploring and plan
                                your next unforgettable stay.
                            </p>

                            {/* HERO STATS */}
                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-bold text-white backdrop-blur-sm">
                                    <Heart
                                        size={15}
                                        className="fill-[#A5E8B8] text-[#A5E8B8]"
                                    />
                                    {total} Saved Items
                                </span>

                                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-bold text-white backdrop-blur-sm">
                                    <Hotel size={15} className="text-[#A5E8B8]" />
                                    {hotels.length} Hotels
                                </span>

                                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-bold text-white backdrop-blur-sm">
                                    <MapPin size={15} className="text-[#A5E8B8]" />
                                    {areas.length} Areas
                                </span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* =====================================================
            WISHLIST CONTENT
        ===================================================== */}

                <section className="bg-[#F5FAF7] py-16 sm:py-20">
                    <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">

                        {/* SECTION HEADER */}
                        <div className="mb-10 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">

                            <div>
                                <div className="mb-4 flex items-center gap-3">
                                    <span className="h-[2px] w-8 bg-[#168B43]" />

                                    <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#168B43]">
                                        Your Personal Collection
                                    </p>
                                </div>

                                <h2 className="text-[33px] font-extrabold tracking-tight text-[#17324D] sm:text-[45px]">
                                    My{" "}
                                    <span className="text-[#168B43]">
                                        Wishlist.
                                    </span>
                                </h2>

                                <p className="mt-4 max-w-[600px] text-sm leading-8 text-[#718096]">
                                    Browse your saved hotels and favorite Dubai
                                    areas, and pick up your travel planning
                                    right where you left off.
                                </p>
                            </div>

                            {/* TOTAL COUNT */}
                            <div className="inline-flex w-fit items-center gap-3 rounded-2xl border border-[#DCEAE1] bg-white px-5 py-4 shadow-sm">
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                                    <Bookmark size={21} />
                                </div>

                                <div>
                                    <p className="text-[20px] font-extrabold leading-none text-[#17324D]">
                                        {total}
                                    </p>

                                    <p className="mt-1 text-xs font-semibold text-[#718096]">
                                        {total === 1 ? "Saved Favorite" : "Saved Favorites"}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* =================================================
                TABS
            ================================================= */}

                        <div className="mb-9 flex flex-wrap items-center justify-between gap-5 border-b border-[#DCEAE1] pb-5">

                            <div
                                role="tablist"
                                aria-label="Wishlist categories"
                                className="flex flex-wrap items-center gap-3"
                            >
                                {tabs.map((entry) => {
                                    const Icon = entry.icon;
                                    const active = tab === entry.id;

                                    return (
                                        <button
                                            key={entry.id}
                                            type="button"
                                            role="tab"
                                            aria-selected={active}
                                            onClick={() => setTab(entry.id)}
                                            className={`inline-flex min-h-[49px] items-center gap-3 rounded-xl px-5 py-3 text-[13px] font-extrabold transition-all ${active
                                                ? "bg-[#168B43] text-white shadow-md shadow-[#168B43]/15"
                                                : "border border-[#DCEAE1] bg-white text-[#17324D] hover:border-[#168B43] hover:text-[#168B43]"
                                                }`}
                                        >
                                            <Icon size={18} />

                                            {entry.label}

                                            <span
                                                className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-[11px] font-extrabold ${active
                                                    ? "bg-white/20 text-white"
                                                    : "bg-[#E8F7EE] text-[#168B43]"
                                                    }`}
                                            >
                                                {entry.count}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            <Link
                                to="/hotels"
                                className="inline-flex items-center gap-2 text-[13px] font-extrabold text-[#168B43] transition hover:gap-3"
                            >
                                Explore More Hotels
                                <ArrowRight size={17} />
                            </Link>
                        </div>

                        {/* STORAGE NOTICE */}
                        {notice && (
                            <div
                                role="alert"
                                className="mb-6 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700"
                            >
                                {notice}
                            </div>
                        )}

                        {/* =================================================
                SAVED ITEMS
            ================================================= */}

                        {items.length > 0 ? (
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {items.map((item) => (
                                    <SavedCard
                                        key={item.id}
                                        item={item}
                                        type={tab}
                                        onRemove={() => remove(item)}
                                        onOpen={() => open(item)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <EmptyWishlist
                                type={tab}
                                onExplore={() =>
                                    navigate(
                                        tab === "hotels"
                                            ? "/hotels"
                                            : "/#popular-areas"
                                    )
                                }
                            />
                        )}

                        {/* BOTTOM INFO */}
                        {items.length > 0 && (
                            <div className="mt-9 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[#DCEAE1] bg-white px-6 py-5">
                                <p className="flex items-center gap-2 text-[13px] font-semibold text-[#718096]">
                                    <ShieldCheck
                                        size={17}
                                        className="text-[#168B43]"
                                    />
                                    Your saved favorites are stored in this browser.
                                </p>

                                <span className="text-xs font-bold text-[#168B43]">
                                    {items.length}{" "}
                                    {items.length === 1 ? "Item" : "Items"} Displayed
                                </span>
                            </div>
                        )}
                    </div>
                </section>

                {/* =====================================================
            DISCOVER HOTELS CTA
        ===================================================== */}

                <section className="bg-white py-16 sm:py-24">
                    <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">

                        <div className="relative isolate overflow-hidden rounded-[24px] bg-[#064E3B] px-6 py-14 text-center sm:px-12 sm:py-20">

                            <img
                                src={heroImg}
                                alt=""
                                loading="lazy"
                                className="absolute inset-0 -z-20 h-full w-full object-cover"
                            />

                            <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28]/95 via-[#064E3B]/90 to-[#064E3B]/75" />

                            <div className="mx-auto max-w-[720px]">
                                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-white/25 bg-white/10 text-[#B6F2CB]">
                                    <Compass size={27} />
                                </div>

                                <p className="mt-6 text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#B6F2CB]">
                                    More To Discover
                                </p>

                                <h2 className="mt-4 text-[33px] font-extrabold leading-tight tracking-tight text-white sm:text-[48px]">
                                    Your Next Dubai Stay
                                    <br />
                                    Is Waiting.
                                </h2>

                                <p className="mx-auto mt-5 max-w-[560px] text-sm leading-8 text-white/75">
                                    Discover more Dubai hotels, explore new
                                    destinations, and save your favorites
                                    for your next trip.
                                </p>

                                <Link
                                    to="/hotels"
                                    className="mt-8 inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white transition hover:bg-[#087D37]"
                                >
                                    Discover Dubai Hotels
                                    <ArrowRight size={18} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
}
